import { action, makeObservable, observable } from "mobx";
import SwayyInvoiceAPI from "../api/SwayyInvoiceAPI";
import {
  GetSwayyInvoicesParams,
  SwayyInvoice,
  SwayyInvoicesPagination,
} from "../../../models/SwayyInvoice/SwayyInvoice";

export const DEFAULT_SWAYY_INVOICE_LIST_PARAMS: GetSwayyInvoicesParams = {
  page: 1,
  limit: 20,
  order_id: "",
  shipment_id: "",
  status: "",
  type: "invoice",
  q: "",
};

export class SwayyInvoiceStore {
  swayyInvoiceAPI: SwayyInvoiceAPI;
  invoices: SwayyInvoice[];
  pagination: SwayyInvoicesPagination;
  params: GetSwayyInvoicesParams;
  filtersOpen: boolean;
  searchDraft: string;
  draftStatus: string[];
  draftType: string[];
  loading: boolean;
  error: string | null;
  actionLoadingId: number | null;

  constructor() {
    this.swayyInvoiceAPI = new SwayyInvoiceAPI();
    this.invoices = [];
    this.pagination = { total: 0, page: 1, limit: 20 };
    this.params = { ...DEFAULT_SWAYY_INVOICE_LIST_PARAMS };
    this.filtersOpen = true;
    this.searchDraft = "";
    this.draftStatus = [];
    this.draftType = ["invoice"];
    this.loading = false;
    this.error = null;
    this.actionLoadingId = null;

    makeObservable(this, {
      invoices: observable,
      pagination: observable,
      params: observable,
      filtersOpen: observable,
      searchDraft: observable,
      draftStatus: observable,
      draftType: observable,
      loading: observable,
      error: observable,
      actionLoadingId: observable,
      getInvoices: action,
      toggleFiltersOpen: action,
      setSearchDraft: action,
      applySearch: action,
      setDraftStatus: action,
      setDraftType: action,
      applyFilters: action,
      clearFilters: action,
      handlePageChange: action,
      handleRowsPerPageChange: action,
      initializeParamsFromSearch: action,
      buildSearchParams: action,
      regenerateInvoice: action,
      reset: action,
    });
  }

  getInvoices = async (params: GetSwayyInvoicesParams = this.params) => {
    this.loading = true;
    this.error = null;
    try {
      const response = await this.swayyInvoiceAPI.getInvoices(params);
      this.invoices = response.invoices;
      this.pagination = response.pagination;
      return response;
    } catch (error: any) {
      this.error = error?.message || "Failed to fetch invoices";
      throw error;
    } finally {
      this.loading = false;
    }
  };

  initializeParamsFromSearch = (searchParams: URLSearchParams) => {
    const page = Math.max(
      1,
      parseInt(searchParams.get("page") || "1", 10) || 1,
    );
    const parsedLimit = parseInt(searchParams.get("limit") || "20", 10);
    const limit = [10, 20, 50, 100].includes(parsedLimit) ? parsedLimit : 20;
    const q = searchParams.get("q") || "";
    const status = searchParams.get("status") || "";
    const type = searchParams.get("type") || "invoice";

    this.params = {
      page,
      limit,
      q,
      status,
      type,
      order_id: "",
      shipment_id: "",
    };
    this.searchDraft = q;
    this.draftStatus = status ? [status] : [];
    this.draftType = type ? [type] : ["invoice"];
  };

  buildSearchParams = (): URLSearchParams => {
    const searchParams = new URLSearchParams();
    const { params } = this;
    if (params.page > 1) searchParams.set("page", String(params.page));
    if (params.limit && params.limit !== 20) {
      searchParams.set("limit", String(params.limit));
    }
    if (params.q) searchParams.set("q", params.q);
    if (params.status) searchParams.set("status", params.status);
    if (params.type && params.type !== "invoice") {
      searchParams.set("type", params.type);
    }
    return searchParams;
  };

  toggleFiltersOpen = () => {
    this.filtersOpen = !this.filtersOpen;
  };

  setSearchDraft = (value: string) => {
    this.searchDraft = value;
  };

  applySearch = () => {
    const q = this.searchDraft.trim();
    if (this.params.q === q) return;
    this.params = { ...this.params, q, page: 1 };
  };

  setDraftStatus = (status: string[]) => {
    this.draftStatus = status;
  };

  setDraftType = (type: string[]) => {
    this.draftType = type;
  };

  applyFilters = () => {
    this.params = {
      ...this.params,
      q: this.searchDraft.trim(),
      status: this.draftStatus[0] || "",
      type: this.draftType[0] || "invoice",
      page: 1,
    };
  };

  clearFilters = () => {
    this.searchDraft = "";
    this.draftStatus = [];
    this.draftType = ["invoice"];
    this.params = {
      ...DEFAULT_SWAYY_INVOICE_LIST_PARAMS,
      limit: this.params.limit,
    };
  };

  handlePageChange = (page: number) => {
    this.params = { ...this.params, page };
  };

  handleRowsPerPageChange = (limit: number) => {
    this.params = { ...this.params, limit, page: 1 };
  };

  regenerateInvoice = async (invoiceId: number) => {
    this.actionLoadingId = invoiceId;
    try {
      await this.swayyInvoiceAPI.regenerateInvoice(invoiceId);
      await this.getInvoices();
    } catch (error) {
      throw error;
    } finally {
      this.actionLoadingId = null;
    }
  };

  reset = () => {
    this.invoices = [];
    this.pagination = { total: 0, page: 1, limit: 20 };
    this.params = { ...DEFAULT_SWAYY_INVOICE_LIST_PARAMS };
    this.filtersOpen = true;
    this.searchDraft = "";
    this.draftStatus = [];
    this.draftType = ["invoice"];
    this.loading = false;
    this.error = null;
    this.actionLoadingId = null;
  };
}

export const swayyInvoiceStore = new SwayyInvoiceStore();
